import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */
/* @jsx mdx */
import DefaultLayout from "/home/runner/work/design/design/node_modules/@primer/gatsby-theme-doctocat/src/components/layout.js";
import ComponentLayout from '~/src/layouts/component-layout';
import { AccessibilityLink } from '~/src/components/accessibility-link';
export const _frontmatter = {};
const layoutProps = {
  _frontmatter
};
const MDXLayout = ComponentLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">





    <img width="960" alt="An image displaying each header from h1 to h6" src="https://user-images.githubusercontent.com/586552/217705129-6422d0c0-dacc-4c98-90c0-9380fb001e83.png" />
    <h2>{`Usage`}</h2>
    <p>{`Headings are used to structure the content on the page, with each heading representing a different level of importance. It is recommended to use headings in a logical manner to create a clear hierarchy and improve accessibility.`}</p>
    <ul>
      <li parentName="ul">{`Heading should be used to communicate page organization and hierarchy.`}</li>
      <li parentName="ul">{`Use heading as the title of a section or sub section.`}</li>
      <li parentName="ul">{`Do not use heading for styling alone. For simply styling text, use text with relevant styling.`}</li>
    </ul>
    <p>{`The heading component will render an html h2 tag without any default styling.`}</p>
    <h2>{`Accessibility and usability expectations`}</h2>
    <p>{`A heading is used to provide a description to the section of content that follows it. There are, therefore, a number of expectations for the accessibility and usability of headings:`}</p>
    <ul>
      <li parentName="ul">{`The text of the heading is descriptive of the content that follows it.`}</li>
      <li parentName="ul">{`All content associated with the heading follows it in the document source order. While content may be visually styled to appear before the heading, for example a tag that provides categorisation or other content, it should be placed after the heading in the document source order.`}</li>
      <li parentName="ul">{`Overall, heading levels must reflect a logical, page-wide hierarchy, similar to a book table of contents. If the content related to a heading includes further sub-headings, these must hierarchically be at least on level deeper. Ideally, no heading levels should be skipped. For example, an `}<inlineCode parentName="li">{`<h2>`}</inlineCode>{` should be followed by an `}<inlineCode parentName="li">{`<h3>`}</inlineCode>{`. However, there may be situations where this is not possible (generally, when content is included/embedded dynamically) – in these cases, it is acceptable to skip levels, as long as the sub-headings don't have the same or higher level as their logical "parent" heading.`}</li>
    </ul>
    <p>{`When using a heading, the level of the heading must be appropriate for its place within the structure of the document, and this should be annotated in designs when possible.`}</p>
    <p>{`While sighted users rely on visual cues such as font size changes to determine what the heading is, assistive technology users rely on programatic cues that can be read out. When text on a page is visually implied to be a heading, ensure that it is coded as a heading. Additionally, visually implied heading level and coded heading level should be consistent. See `}<a parentName="p" {...{
        "href": "https://www.w3.org/WAI/WCAG21/Understanding/info-and-relationships.html"
      }}>{`WCAG success criteria: 1.3.1: Info and Relationships`}</a>{`.`}</p>
    <p>{`Headings allow assistive technology users to quickly navigate around a page. Navigation to text that is not meant to be a heading can be a confusing experience. `}<a parentName="p" {...{
        "href": "https://www.w3.org/WAI/tutorials/page-structure/headings/"
      }}>{`Learn more about best heading practices (WAI Headings)`}</a>{`.`}</p>
    <h3>{`Built-in accessibility features`}</h3>
    <p>{`The heading component exposes both the heading role and heading level.`}</p>
    <p>{`The heading component provides styles with appropriate text sizes to provide a visual hierarchy when the appropriate level is set.`}</p>
    <p>{`The default heading text has sufficient color contrast against a white background.`}</p>
    <h3>{`Handling subheadings, eyebrows, and straplines`}</h3>
    <p>{`Eyebrows are "subheading" treatments, where the heading text's visual treatment includes a word or phrase placed above or below the main heading in a smaller font size.`}</p>
    <p>{`Eyebrows should be incorporated as part of the heading element, and have their visual styling modified via a `}<inlineCode parentName="p">{`span`}</inlineCode>{` element applied to the eyebrow word or phrase. Primer `}<a parentName="p" {...{
        "href": "/foundations/css-utilities/typography/"
      }}>{`typography utility styles`}</a>{` can be used to accomplish this.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-html"
      }}>{`<h2>
  <span class="h4 lh-condensed text-uppercase">
    Eyebrow
  </span>
  Heading
</h2>
`}</code></pre>
    <p>{`Straplines are short pieces of text that are used to augment and provide additional context or information about the heading. They augment the heading content, but are not necessary for understanding. Because of this, the strapline content is not included in the heading element.`}</p>
    <p>{`A parent landmark element must be used to communicate the programatic association between the heading and its strapline.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-html"
      }}>{`<header>
  <h2>
    Heading
  </h2>
  <p>
    Strapline
  </p>
</header>
`}</code></pre>
    <h3>{`Implementation requirements`}</h3>
    <p>{`When using the component, configure it with the heading level provided in annotated designs or with the appropriate level for the structure of the document and content. It should be noted that the visual design may not always match the annotated heading level, and the annotation should always supersede the visual in the implementation if they differ.`}</p>
    <h3>{`How to test the component`}</h3>
    <h4>{`Integration tests`}</h4>
    <ul>
      <li parentName="ul">{`The text of the heading is descriptive of the content that follows it.`}</li>
      <li parentName="ul">{`The heading is of the appropriate level for the structure of the document and content.`}</li>
      <li parentName="ul">{`The heading is followed by content related to the heading or a heading of one level deeper.`}</li>
      <li parentName="ul">{`All content that relates to the heading follows it in the document source order, even if it appears visually before the heading.`}</li>
    </ul>
    <h4>{`Component tests`}</h4>
    <ul>
      <li parentName="ul">{`The component exposes the heading role.`}</li>
      <li parentName="ul">{`The component exposes the heading's level.`}</li>
    </ul>
    <h3>{`Known accessibility issues (GitHub staff only)`}</h3>
 <AccessibilityLink label="Heading" mdxType="AccessibilityLink" />

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      